<template>
  <div id="leaf-relief" class="wwf-form donation-form leaf-relief-form">
    <!-- Header -->
    <LeafReliefHeader
      :datalayerName="datalayerName"
      :counterLimit="counterLimit"
      :targetText="targetText"
      :headerText="headerText"
    ></LeafReliefHeader>

    <!-- Form -->
    <div class="processing-container">
      <div class="processing" v-if="isProcessing">
        <i class="fas fa-sync-alt fa-spin"></i>
      </div>
      <Stepper
        :petitionSelector="petitionSelector"
        :donationSelector="donationSelector"
        :petitionHandle="petitionHandle"
        :programName="programName"
        :datalayerName="datalayerName"
        :thankYouText="thankYouText"
        :getSocialText="getSocialText"
        :duplicateEmailMessage="duplicateEmailMessage"
        v-if="!!product"
      ></Stepper>
    </div>

    <!-- Last signatures - Always present whatever step we are-->
    <div class="last-signatures">
      <div class="ls-title">Recent Supporters</div>
      <LastSignatures
        campaigId="leaf-relief"
        classes="ra-last-signatures"
      ></LastSignatures>
    </div>
  </div>
</template>

<script>
import LeafReliefHeader from "./Header";
import Stepper from "./Stepper.vue";
import LastSignatures from "./LastSignatures";

import {
  getParameterByName,
  getNameByDonationType,
  getNameByPaymentType,
  removeURLParameter,
} from "../../helpers";
import gtm from "../../utils/gtm";
import { mapState } from "vuex";

export default {
  name: "LeafRelief",
  components: {
    LeafReliefHeader,
    Stepper,
    LastSignatures,
  },

  data() {
    return {
      defaultDonationType: null,
      isProcessing: false,
    };
  },
  computed: mapState({
    donation: (state) => state.donation.donation,
    product: (state) => state.product.product,
    supporter: (state) => state.leafRelief.details,
  }),
  props: {
    productId: {
      type: String,
      required: true,
    },
    datalayerName: {
      type: String,
      required: true,
    },

    programName: {
      type: String,
      required: true,
    },

    // Selectors
    petitionSelector: {
      type: String,
      default: "Plant a Tree",
    },
    donationSelector: {
      type: String,
      default: "Plant a Forest",
    },

    // Header
    targetText: {
      type: String,
      default: "1 Million",
    },
    headerText: {
      type: String,
      default:
        "Join us in our quest to plant 2 billion trees and restore our unique habitat",
    },
    counterLimit: {
      type: Number,
      default: 100,
    },

    // Details
    petitionHandle: {
      type: String,
      default: "Show your support and we'll plant a tree today",
    },
    duplicateEmailMessage: {
      type: String,
      default:
        "You can donate multiple times with the same email but you can only submit the petition once.",
    },

    // Thank You
    thankYouText: {
      type: String,
      default: "Thank you for helping repopulate our forests",
    },
    getSocialText: {
      type: String,
      default:
        "Share this opportunity with friends to let them plant their own seed",
    },
  },

  async created() {
    // Fetch product informations
    // We can have query params to override the appeal and fund id
    await this.$store.dispatch("getProductById", {
      id: this.productId,
      source: window.location.href,
      appealid: getParameterByName("t"),
      fundid: getParameterByName("f"),
    });

    this.checkPaypalCallback();
    this.readQueryParams();
    this.setDonationType();
  },
  methods: {
    async checkPaypalCallback() {
      try {
        // Check if there payment paypal pending to excecute
        const paymentId = getParameterByName("token");
        const payerId = getParameterByName("PayerID");

        if (payerId && paymentId) {
          console.log("Paypal Callback - Payment found");

          this.isProcessing = true;

          // Move to top of the form
          const el = document.getElementById("from-top");
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          }

          const paypalDonation = await this.$store.dispatch("paymentPaypal", {
            payerid: payerId,
            paymentid: paymentId,
          });

          console.log("Paypal Callback - Donaion", paypalDonation);

          if (paypalDonation) {
            // Remove the query params from the URL
            let querylessUrl = removeURLParameter("token");
            querylessUrl = removeURLParameter("PayerID", querylessUrl);
            window.history.pushState({}, document.title, querylessUrl);

            gtm.pushPurchase(
              paypalDonation.key,
              paypalDonation.productname,
              paypalDonation.productid,
              paypalDonation.producttype,
              getNameByDonationType(paypalDonation.type),
              paypalDonation.amount,
              paypalDonation.postcode,
              getNameByPaymentType("wwf_paypal")
            );

            // Set data and send to thank you page
            this.$store.commit("updateSelection", "donation");
            this.supporter.firstName = paypalDonation.firstname;
            this.supporter.lastName = paypalDonation.lastname;
            this.donation.amount = paypalDonation.amount;

            if (paypalDonation.extra.agreesDisplayName) {
              await this.$store.dispatch("saveSignature", {
                campaigId: this.datalayerName,
                firstName: this.supporter.firstName,
                lastName: this.supporter.lastName,
                isDonation: true,
              });
            }

            // Go to thank you page
            this.$store.commit("updateLeafRelief", {
              field: "currentStep",
              value: 2,
            });

            // Move to top of the form
            const el = document.getElementById("from-top");
            if (el) {
              el.scrollIntoView({ behavior: "smooth" });
            }
          }
        }
      } catch (err) {
        console.error("Paypal Callback - error", err);
      } finally {
        this.isProcessing = false;
      }
    },
    readQueryParams() {
      // Check if there are some query string in URL to prefill the form, Query from donate widget
      const q = getParameterByName("q");
      if (q) {
        const paramsPrefill = "?" + window.atob(q);
        console.log("paramsPrefill", paramsPrefill);

        const amount = getParameterByName("amount", paramsPrefill);
        const email = getParameterByName("email", paramsPrefill);
        const firstname = getParameterByName("firstname", paramsPrefill);
        const lastname = getParameterByName("lastname", paramsPrefill);
        const salutation = getParameterByName("salutation", paramsPrefill);
        const wildcardCode = getParameterByName("wildcardCode", paramsPrefill);

        if (wildcardCode) {
          this.donation.packagecode = wildcardCode;
        }

        let dollarhandle = getParameterByName("dollarhandle", paramsPrefill)
          ? getParameterByName("dollarhandle", paramsPrefill).replace("$", "")
          : undefined;
        let dollarhandles = getParameterByName("dollarhandles", paramsPrefill);

        if (dollarhandles) {
          // get dollarhandles from query params
          dollarhandles = dollarhandles.replace(/[^0-9\. ]+/g, "").split(" ");
          const newDollarHandles = [];
          dollarhandles.forEach((v, index) => {
            // Check if there is a product dollar handles index
            if (this.product.dollarhandles.Single[index]) {
              newDollarHandles.push({
                amount: v,
                img: this.product.dollarhandles.Single[index].img,
                text: this.product.dollarhandles.Single[index].text,
              });
            }
          });
          this.product.dollarhandles.Single = newDollarHandles;
        }

        this.donation.dollarhandle = dollarhandle;
        this.donation.amount = amount;
        this.donation.title = salutation;

        this.supporter.email = email;
        this.supporter.firstName = firstname;
        this.supporter.lastName = lastname;
      }

      // Pre select Petition or donation (ST = Submission type)
      const st = getParameterByName("st");
      if (!!st && (st === "petition" || st === "donation")) {
        this.$store.commit("updateSelection", st);
      }
    },
    setDonationType() {
      // Set default donation type by query param
      const dt = getParameterByName("dt");
      if (dt && (dt === "Single" || dt === "Regular")) {
        this.defaultDonationType = dt;
      }

      // DONATION TYPE - Pre-select the doallar handle and the package (rule: first item on the list)
      // As we only have Single and Regular, always set Single as the first option
      const defaultTypeExists =
        this.product.donationtypes.filter((e) => e === this.defaultDonationType)
          .length === 1;
      let defaultType =
        defaultTypeExists && this.defaultDonationType
          ? this.defaultDonationType
          : this.product.appealcodesetting.defaultdonationtype;

      // Reverse alphabetical order
      this.product.donationtypes = this.product.donationtypes.sort((a, b) => {
        if (a === defaultType) {
          return -1;
        }
        if (b === defaultType) {
          return 1;
        }
        return 0;
      });

      this.donation.type = defaultType;
      const settings = this.product.donationsettings[defaultType];
      if (settings) {
        this.donation.campaignid = settings.campaignid;
        this.donation.fundid = settings.fundid;
      }

      // DOLLAR HANDLE - Select first element
      const handles = this.product.dollarhandles[defaultType];
      if (handles) {
        this.donation.amount = handles[0].amount;
        this.donation.dollarhandle = handles[0].amount;
      }
    },
  },
};
</script>

<style scoped>
.last-signatures {
  padding: var(--lrPadding);
  padding-top: 0;
}

.last-signatures .ls-title {
  color: var(--wwfRegenerateAustralia);
  font-weight: 600;
}

.processing-container {
  position: relative;
}

.processing {
  z-index: 11;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  font-size: 40px;
  color: var(--wwfWhite);
  background-color: #00000075;
}
</style>


// WEBPACK FOOTER //
// LeafRelief.vue?a42ca064